//frontend/src/features/authentication/components/ForgotPasswordModal.tsx

import React, { useState } from 'react';
import { forgotPasswordModalStyles as styles } from '../../../shared/styles/forgotPasswordModalStyles';
import { ROUTE_NOTIFICATIONS_RECOVER_PASSWORD } from '../../../config/routes/routeConfig';

interface ForgotPasswordModalProps {
	isOpen: boolean;
	onClose: () => void;
}

interface ForgotPasswordResponse {
	error?: string;
	message?: string;
	[key: string]: any;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
	isOpen,
	onClose,
}) => {
	const [email, setEmail] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [sent, setSent] = useState<boolean>(false);
	const [error, setError] = useState<string>('');

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		e.preventDefault();
		setError('');
		setLoading(true);

		try {
			const response = await fetch(ROUTE_NOTIFICATIONS_RECOVER_PASSWORD, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email }),
				credentials: 'include',
			});

			const data: ForgotPasswordResponse = await response.json();

			if (response.ok) {
				setSent(true);

			} else {
				setError(data.error || 'Error al enviar correo de recuperación');
			}
		} catch {

			setError('Error en la conexión con el servidor');
		} finally {
			setLoading(false);
		}
	};

	const handleClose = (): void => {
		setEmail('');
		setError('');
		setSent(false);
		setLoading(false);
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className={styles.overlay}>
			<div className={styles.modalContainer}>
				<div className={styles.modal}>
					{/* Header */}
					<div className={styles.header}>
						<button
							onClick={handleClose}
							className={styles.closeButton}>
							<svg
								className={styles.closeIcon}
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M6 18L18 6M6 6l12 12'
								/>
							</svg>
						</button>

						<div className={styles.iconContainer}>
							<svg
								className={styles.icon}
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
								/>
							</svg>
						</div>

						<h2 className={styles.title}>
							{sent ? '¡Correo enviado!' : '¿Olvidaste tu contraseña?'}
						</h2>
						<p className={styles.subtitle}>
							{sent
								? 'Revisa tu correo electrónico y sigue las instrucciones para restablecer tu contraseña.'
								: 'Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.'}
						</p>
					</div>

					{/* Contenido */}
					{sent ? (
						<div className={styles.successContent}>
							<div className={styles.successIcon}>
								<svg
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M5 13l4 4L19 7'
									/>
								</svg>
							</div>
							<p className={styles.successText}>
								Si existe una cuenta con ese correo, recibirás las instrucciones
								en unos minutos.
							</p>
							<button
								onClick={handleClose}
								className={styles.okButton}>
								Entendido
							</button>
						</div>
					) : (
						<form
							onSubmit={handleSubmit}
							className={styles.form}>
							{error && <div className={styles.errorMessage}>{error}</div>}

							<div className={styles.inputGroup}>
								<label
									htmlFor='email'
									className={styles.inputLabel}>
									Correo electrónico
								</label>
								<input
									id='email'
									type='email'
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									className={styles.input}
									placeholder='Ingresa tu correo electrónico'
									required
									disabled={loading}
								/>
							</div>

							<div className={styles.buttonGroup}>
								<button
									type='button'
									onClick={handleClose}
									className={styles.cancelButton}
									disabled={loading}>
									Cancelar
								</button>
								<button
									type='submit'
									className={`${styles.submitButton} ${
										loading ? styles.submitButtonLoading : ''
									}`}
									disabled={loading}>
									{loading ? (
										<>
											<svg
												className={styles.loadingIcon}
												viewBox='0 0 24 24'>
												<circle
													cx='12'
													cy='12'
													r='10'
													stroke='currentColor'
													strokeWidth='4'
													fill='none'
													strokeDasharray='32'
													strokeDashoffset='32'
												/>
											</svg>
											Enviando...
										</>
									) : (
										'Enviar enlace'
									)}
								</button>
							</div>
						</form>
					)}
				</div>
			</div>
		</div>
	);
};

export default ForgotPasswordModal;
